<template>
    <div class="comp-item-status-grid w-100 h-100 d-flex flex-column">

        <div class="d-flex justify-content-between gap-3 mb-2">
            <div class=""></div>
            <div class="">
                <b-button-group v-if="gridCanMount" size="sm" class="">
                    <b-button variant="subtle-primary" class="d-flex align-items-center gap-2" @click="exportExcel">
                        <!-- <b-icon variant="success" icon="file-earmark-excel" class="mr-1"></b-icon> -->
                        <span class="fiv-sqo fiv-icon-xls font-size-100"></span>
                        <span>Export to Excel</span>
                    </b-button>
                </b-button-group>
            </div>
        </div>

        <div 
            class="flex-fill" 
            :style="{maxHeight: gridMaxHeight}"
        >
            <ag-grid-vue
                v-if="gridCanMount"
                ref="theGrid"
                class="ag-theme-compliance-grid w-100 h-100 comp-item-status-grid-aggrid"
                :gridOptions="gridOptions"
                :columnDefs="columnDefs"
                :context="gridContext"
                :rowData="rowData"
                :immutableData="false"
                :sideBar="sideBar"
                :pagination="pagination"
                :paginationPageSize="paginationPageSize"
                @filterChanged="onFilterChanged"
                @firstDataRendered="firstDataRendered"
                @grid-ready="onGridReady"
            ></ag-grid-vue>

            <div 
                v-else
                key="loadingGrid"
                class="h-100 w-100 d-flex align-items-center justify-content-center"
            >
                <div class="text-center">
                    <b-spinner 
                        variant="primary" 
                        class=""
                        style="width: 3rem; height: 3rem;"
                    ></b-spinner>
                    <div class="font-size-120 font-weight-300 text-secondary mt-2 text-center">
                        Loading all documents...
                    </div>
                </div>
            </div>

        </div>

        <!-- DETAIL SIDEBAR v-model="sidebarShowing" -->
        <b-sidebar 
            v-model="sidebarShowing"
            id="vendor-comp-item-detail-sidebar"
            right 
            width="700px"
            shadow
            lazy
            bg-variant="gray-100"
            body-class="py-4 px-3"
            @shown="handleSidebarShown"
            @hidden="handleSidebarHidden"
            @change="handleSidebarChange"
        >
            <!-- <transition name="fade-fast" mode="out-in"> -->
            <!-- TODO - make this work for CompItem/Vendor combos where you don't have VendorCompItem yet 
            make sure to get the right IDs for the right things (keys/sending to API, etc.) 
            -->
            <VendorCompItemDetail
                v-if="sidebarShowing && sidebarActiveVendorCompItemId"
                :key="sidebarActiveVendorCompItemId"
                :vendorCompItemId="sidebarActiveVendorCompItemId"
                @updated="handleVciUpdated"
                @deleted="handleVciUpdated"
            ></VendorCompItemDetail>
            <!-- </transition> -->
        </b-sidebar>  

        <!-- CREATE MODAL -->
         <b-modal
            v-model="addVciModalShowing"
            centered
            size="md"
            hide-footer
            @hidden="handleVciModalHidden"
            :title="`Add Document for ${addingVendorName}`"
         >
            <ScopedVendorCompItemForm
                :vendorId="addingVendorId"
                @cancel="closeVciModal"
                @loading-status="handleAddVciLoadingStatus"
                @saved="handleAddVciSaved"
            ></ScopedVendorCompItemForm>
         </b-modal>

    </div>
</template>

<script>
// import Vue from 'vue';
import { suppliosApi } from '@/js/api-service';
import { errorHandling } from '@/js/custom-vue-mixins';
import { ComplianceItem, VendorCompItemStatus, ComplianceRequirement } from './shared/compModels';
import { AgGridVue } from 'ag-grid-vue';
import sortBy from 'lodash/sortBy';
import groupBy from 'lodash/groupBy';
import VendorCompItemDetail from './VendorCompItemDetail.vue';
import ScopedVendorCompItemForm from './ScopedVendorCompItemForm.vue';
import { CompanyTagListRenderer, CompanyTagSingleRenderer } from '@/components/grids/gridRenderers';
import {
    // COL_KEY_COMP_ITEM_PREFIX,
    getColKey,
    isCompItemColKey,
    ALL_TAGS_ON_VENDOR_KEY,
    NESTED_TAGS_ON_VENDOR_KEY,
    getTagCatColKey,
    isTagColKey,
} from './comp_grids/compGridUtils';
import {
    ReqStatusRenderer,
    CompItemHeaderRenderer,
    VendorColHeaderRenderer,
    VendorColRenderer,
    VendorFloatingFilterComponent,
} from './comp_grids/compGridRenderers';


export default {
    name: 'CompItemReqStatusGrid',
    components: {
        AgGridVue,
        VendorCompItemDetail,
        ScopedVendorCompItemForm,
    },
    mixins: [
        errorHandling,
    ],
    props: {
        gridMaxHeight: {
            type: String,
            required: false,
            default: '100%',
        },
        // TODO: add props for vendorIds, compItemIds, etc. to pre-filter the grid
        // TODO: add local storage stuff to save user's grid settings
    },
    data: function() {
        return {
            // REQ GRID STUFF
            loadingReqGrid: false,

            // GRID STUFF
            loadingGrid: false,
            statusStore: {},  // keyed by unique ID, combination of req ID and vendor ID
            compItems: [],  // list of all comp items we get from API
            vendorsById: {},  // store of vendors
            compItemsById: {},  // store of CompItems
            companyTagsById: {},  // store of company tags
            companyTagCatsById: {},  // store of company tag categories
            includeAllTagsCol: false,  // set to true if we have column for all tags - THIS CAN CAUSE CONFUSION FOR FILTERING!
            compReqs: [],  // list of all compliance requirements we get from API
            compReqsById: {},  // store of compliance requirements
            // SIDEBAR STUFF
            sidebarShowing: false,
            sidebarActiveVendorCompItem: null,  // set full object here to show in sidebar

            // CREATE MODAL STUFF
            addVciModalShowing: false,
            addVciSpecificCompItem: '',  // set the specific compItemId here if you want to add a SPECIFIC column/compItem for a vendor
            addVciActiveRowData: {},  // when we click the add button, we stash entire row data here --  get { name, id } for the vendor info
            // addingCompItemId: '',  // set this to the compItem ID when you want to add a new VendorCompItem to a compItem - LATER
            
            /// GRID STUFF
            //rowHeight: 60,
            gridCanMount: false,  // set to true when we have data and are ready to mount the grid
            gridWrapperStyle: {width: '100%'},  // set it in beforeMount - not an ag-grid thing - set height/min-height at least - also relates to pagination
            //domLayout: 'autoHeight',  // options are 'normal' or 'autoHeight' or printing I think... make sure to also set height/style of wrapper and pagination when changing this... all must work together
            gridOptions: null,
            gridContext: null,
            gridApi: null,
            gridColumnApi: null,
            rowData: null,
            columnDefs: null,
            sideBar: {
                position: 'right',
                defaultToolPanel: 'columns',
                toolPanels: [
                    {
                        id: 'columns',
                        labelDefault: 'Document Types (Columns)',
                        labelKey: 'columns',
                        iconKey: 'columns',
                        toolPanel: 'agColumnsToolPanel',
                        toolPanelParams: {
                            suppressRowGroups: true,
                            suppressValues: true,
                            suppressPivots: true,
                            suppressPivotMode: true,
                            suppressColumnFilter: false,
                            suppressColumnSelectAll: false,
                            suppressColumnExpandAll: true,
                        },
                    },
                    {
                        id: 'filters',
                        labelDefault: 'Vendor Filters',
                        labelKey: 'filters',
                        iconKey: 'filter',
                        toolPanel: 'agFiltersToolPanel',
                    },
                ],
            },
            pagination: false,  // infinite scroll for now
            paginationPageSize: 20,
            paginationAutoPageSize: false,  // if true, paginationPageSize is not used, sets based on size of browser
            pageSizeOptions: [10, 20, 50],  // if using external pagination?
        }
    },
    computed: {
        sidebarActiveVendorCompItemId() {
            return this.sidebarActiveVendorCompItem?.id || '';
        },
        sidebarActiveVendorCompItemTitle() {
            return this.sidebarActiveVendorCompItem?.title || '';
        },
        allCompItemColKeys() {
            return this.compItems.map(ci => getColKey(ci.id));
        },
        addingVendorName() {
            return this.addVciActiveRowData?.name || '';
        },
        addingVendorId() {
            return this.addVciActiveRowData?.id || '';
        },
    },
    created() {},
    beforeMount() {
        // SET GRID / WRAPPER SIZES and LAYOUT
        /* this.gridWrapperStyle = {  // only set this if not doing autoHeight with pagination
            height: '500px',
            minHeight: '500px',
        }; */

        // set gridOptions
        this.gridOptions = {
            components: {},
            frameworkComponents: {
                reqStatusRenderer: ReqStatusRenderer,
                vendorColHeaderRenderer: VendorColHeaderRenderer,
                vendorColRenderer: VendorColRenderer,
                compItemHeaderRenderer: CompItemHeaderRenderer,
                companyTagListRenderer: CompanyTagListRenderer,
                companyTagSingleRenderer: CompanyTagSingleRenderer,
                vendorFloatingFilterComponent: VendorFloatingFilterComponent,
            },
            getRowNodeId: function(data) {
                return data ? data.id : '';  // should return strings
            },  // use this to have grid use your own defined node IDs... or let it use its own.  should return strings.
            popupParent: document.querySelector('body'),  // needed so popups overflow outside the grid
            groupUseEntireRow: true,  // makes the group rows take up entire row, not just one column
            suppressContextMenu: true,  // turn off right-click context menu
            groupHeaderHeight: 40,
            headerHeight: 70,
            columnTypes: {
                compItem: {},
                tagCategory: {}, // maybe add stuff here later...
            },
        };
        this.gridContext = {
            componentParent: this,  // then child 'framework components' inside here can access this 'parent' component via this.params.context.componentParent
        };
    },
    mounted() {
        this.getGrid();
    },
    methods: {
        async getGrid() {
            this.loadingGrid = true;
            this.gridCanMount = false;
            let resp;
            try {
                resp = await suppliosApi.compItemStd.get_req_status_grid_data();
            } catch (error) {
                this.apiErrorHandler(error);
            }
            if (resp?.data) {
                // SCHEMA: { compliance_items, vendors, status_list, need_status_list, company_tags, company_tag_categories }
                // status_list is ONE PER vendor/compItem combo
                const { compliance_requirements, compliance_items, vendors, status_list, need_status_list, company_tags, company_tag_categories } = resp.data;
                const vendorList = vendors || [];
                this.vendorsById = Object.fromEntries(vendorList.map(v => [v.id, v]));
                const compReqListRaw = compliance_requirements || [];
                const compReqList = compReqListRaw.map(c => new ComplianceRequirement(c));
                this.compReqs = compReqList;
                this.compReqsById = Object.fromEntries(compReqList.map(cr => [cr.id, cr]));
                const compItemListRaw = compliance_items || [];
                const compItemList = compItemListRaw.map(c => new ComplianceItem(c));
                this.compItems = compItemList;
                this.compItemsById = Object.fromEntries(compItemList.map(ci => [ci.id, ci]));
                const compItemVendorStatusListRaw = status_list || [];  // the raw VCI objects from API
                const compItemVendorStatusList = compItemVendorStatusListRaw.map(s => this.makeLocalCompItemVendorStatus(s));
                // build store of status objects, keyed by unique ID (their ID is already unique - either the VCI ID or combined CompItem/Vendor ID)
                // still needed?                
                // this.statusStore = Object.fromEntries(statusList.map(s => [s.id, s]));
                const needStatusList = need_status_list;

                // ADD THINGS TO GRID CONTEXT - collect things along the way, then add all at the end 
                const addToContext = {};
                addToContext['needStatus'] = Object.fromEntries(needStatusList.map(s => [s.key, s]));
                addToContext['compItemsById'] = this.compItemsById;  // so renderers can get info about the comp items

                // HANDLE TAGS
                const tagCats = company_tag_categories || [];
                const tags = company_tags || [];
                // must put tags in grid context so we can use them in the column defs
                // add to context under the 'comopany_tags' key -- that's what renderers expect
                const tagsById = Object.fromEntries(tags.map(t => [t.id, t]));
                const tagCatsById = Object.fromEntries(tagCats.map(tc => [tc.id, tc]));
                this.companyTagsById = tagsById;
                this.companyTagCatsById = tagCatsById;
                addToContext['company_tags'] = tagsById;
                addToContext['company_tag_categories'] = tagCatsById;
                const tagColDefs = this.buildTagColDefs(tagCats, tags);
                
                // BUILD colDefs
                // const allColKeys = compItemList.map(ci => getColKey(ci.id));  // safe to copy just using spread ...
                const compItemColDefs = this.buildCompItemColDefs(compItemList);
                const sortedCompItemColDefs = sortBy(compItemColDefs, ['headerName']);
                const compItemColGroup = {
                    headerName: `Document Types (${this.compItems.length})`,
                    headerClass: 'bg-primary-200 font-size-130',
                    toolPanelClass: 'font-weight-bold bg-primary-200 rounded-lg',
                    marryChildren: true,
                    children: [...sortedCompItemColDefs],
                };
                const tagColGroup = {
                    headerName: 'Vendor Tags',
                    headerClass: 'bg-accent-200 font-size-130',
                    toolPanelClass: 'font-weight-bold bg-accent-200 rounded-lg',
                    marryChildren: true,
                    children: tagColDefs,
                };
                this.columnDefs = [this.buildVendorColDef(), compItemColGroup, tagColGroup];
                console.log('columnDefs', this.columnDefs);

                // ROWS will be vendors -- COLUMNS will be CompItems
                // because you have many more vendors than CompItems
                // NOTE: we might have MULTIPLE VCIs (and therefore status objects) for a single CompItem/Vendor pair
                //       so we need to have ARRAY of status objects in each cell
                // NOTE: with the new API, we get a list of status objects for each vendor/compItem combo!

                const statusObjectsByVendorId = groupBy(compItemVendorStatusList, 'vendorId');  // will likely NOT have all vendors... only the ones with at least one vci
                const allRows = [];
                vendorList.forEach(vObj => {
                    const statusObjectsForVendor = statusObjectsByVendorId[vObj.id] || [];
                    const singleRow = this.buildSingleReqRowData(vObj, statusObjectsForVendor);
                    allRows.push(singleRow);
                });
                // now we sort rows by vendor name 
                const lowercaseName = (v) => v.name.toLowerCase();
                this.rowData = sortBy(allRows, [lowercaseName]);  // need to sort them alphabetically by name?                
                console.log('rowData', this.rowData);
                
                // ADD THINGS TO CONTEXT - have to do it with $set so Vue can track it
                Object.entries(addToContext).forEach(([key, value]) => {
                    this.$set(this.gridContext, key, value);
                });
            }
            this.gridCanMount = true;
            this.loadingGrid = false;
        },
        async refreshSingleVendor(vendorId) {
            // TODO
            console.log('refreshSingleVendor', vendorId);
            let resp;
            try {
                resp = await suppliosApi.compItemStd.get_req_status_grid_data({vendorIds: [vendorId]});
                const statusObjectsRaw = resp.data.status_list || [];
                const statusObjects = statusObjectsRaw.map(s => this.makeLocalCompItemVendorStatus(s));
                const newVendorObj = resp.data.vendors[0];
                const newRowData = this.buildSingleReqRowData(newVendorObj, statusObjects);
                // for now we use this.$set to replace the item in the array:  Vue.set(vm.items, indexOfItem, newValue)
                const indexOfVendor = this.rowData.findIndex((r) => r.id === vendorId);
                if (indexOfVendor !== -1) {
                    console.log(`WILL UPDATE ROW DATA FOR VENDOR ID ${vendorId} TO NEW DATA:`, newRowData);
                    this.$set(this.rowData, indexOfVendor, newRowData)
                }
            } catch (error) {
                this.apiErrorHandler(error);
            }
        },
        async refreshSingleVendorOLD(vendorId) {
            // call API to get new data for a SINGLE VENDOR / ROW - and update the grid / local stores
            // TODO - set that row to loading - or keep loading flags in separate store???
            //const vendorObj = this.vendorsById[vendorId];
            let resp;
            try {
                resp = await suppliosApi.compItemStd.refresh_status_grid_data(vendorId);
                const statusObjectsRaw = resp.data.status_list || [];
                const newVendorObj = resp.data.vendor || {};
                const statusObjectsForVendor = statusObjectsRaw.map(s => this.makeLocalStatusObject(s));
                const newRowData = this.buildSingleRowData(newVendorObj, statusObjectsForVendor);
                // for now we use this.$set to replace the item in the array:  Vue.set(vm.items, indexOfItem, newValue)
                const indexOfVendor = this.rowData.findIndex((r) => r.id === vendorId);
                if (indexOfVendor !== -1) {
                    console.log(`WILL UPDATE ROW DATA FOR VENDOR ID ${vendorId} TO NEW DATA:`, newRowData);
                    this.$set(this.rowData, indexOfVendor, newRowData)
                }
            } catch (error) {
                this.apiErrorHandler(error);
            }
        },
        makeLocalCompItemVendorStatus(compItemVendorStatusFromApi) {
            // each of these is one CELL in the grid -- with zero or multiple VCI status objects inside
            // schema: { id, req_id, vendor_id, comp_item_id, comp_item_type_id, title, vendor_name, req_status, vci_status_objects }
            const { vci_status_objects, ...otherThings } = compItemVendorStatusFromApi;
            const vciStatusObjects = vci_status_objects.map(this.makeLocalStatusObject);  // maybe you don't need all this junk, but easy to drop-in to existing stuff
            return {
                ...otherThings,
                vendorId: compItemVendorStatusFromApi.vendor_id,
                compItemId: compItemVendorStatusFromApi.comp_item_id,
                vci_status_objects: vciStatusObjects,
            };
        },
        makeLocalStatusObject(vciFromApi) {
            return {
                id: vciFromApi.id,
                vendorId: vciFromApi.vendor_id,
                compItemId: vciFromApi.comp_item_id,
                status: new VendorCompItemStatus(vciFromApi),
                loading: false,
                details: {},  // if you want to just get simple status, then fetch details on demand
            };
        },
        buildSingleReqRowData(vendorObj, compItemVendorStatusObjects) {
            // for the new version where each status object is a single cell
            const statusObjectsByCompItemColKey = Object.fromEntries(compItemVendorStatusObjects.map(s => [getColKey(s.compItemId), s]));
            const singleRowData = {
                ...vendorObj,  // nest this in 'vendor' key instead?
                ...statusObjectsByCompItemColKey,  // our 'empty' status - keys for each compItem, values are null right now (doing it this way because this used to be empty array)
            };
            // FOR TAGS - we will also combine all the tags for each vendor into a single list (just tag IDs)
            const allTagIds = Object.values(singleRowData[NESTED_TAGS_ON_VENDOR_KEY] || {}).flat();  // gets the tags_by_category for this vendor
            singleRowData[ALL_TAGS_ON_VENDOR_KEY] = allTagIds;
            return singleRowData;
        },
        buildSingleRowData(vendorObj, localStatusObjectsForVendor) {
            // build AND RETURN (do not set) data for a single row in the grid
            // NOTE: we could potentially do this on backend... try it?
            // MUST have all columns constructed already and this.compItems must be set
            // separated this out so we can build a single row for refreshing a single vendor
            // vendorObj is the vendor object from the API { id, name, url, tags_by_category }
            // localStatusObjectsForVendor is the list of LOCAL status objects for this vendor 
            const statusByColKey = Object.fromEntries(this.allCompItemColKeys.map(k => [k, []]));  // our 'empty' object that will hold status objects
            const singleRowData = {
                ...vendorObj,  // nest this in 'vendor' key instead?
                ...statusByColKey,  // our 'empty' status - keys for each compItem, values are ARRAY of status objects (currently empty)
            };
            // console.log('in buildSingleRowData - singleRowData is', singleRowData);
            // now loop through status objects and add them to the row in statusByColKey
            localStatusObjectsForVendor.forEach(s => {
                const colKey = getColKey(s.compItemId);
                // console.log('will try to set this colKey', colKey, 'from this localStatusObject', s);
                singleRowData[colKey].push(s);
            });
            // FOR TAGS - we will also combine all the tags for each vendor into a single list (just tag IDs)
            const allTagIds = Object.values(singleRowData[NESTED_TAGS_ON_VENDOR_KEY] || {}).flat();
            singleRowData[ALL_TAGS_ON_VENDOR_KEY] = allTagIds;
            return singleRowData;
        },
        buildTagColDefs(tagCats, tags) {
            console.log('buildTagColDefs', tagCats, tags);
            const tagsByCatId = groupBy(tags, 'category_id');
            // build ONE with ALL tags, then one for each category
            const allTagNamesById = Object.fromEntries(tags.map(t => [t.id, t.name]));
            const defaultTagColDef = {  // you COULD just use the 'type' feature of ag-grid, but I find this easier...
                headerClass: 'text-center bg-accent-100',
                cellClass: 'text-center',
                toolPanelClass: 'bg-accent-100',
                resizable: true,
                maxWidth: 200,
                autoHeight: true,
                wrapText: true,
                suppressMenu: false,
                filter: true,
                floatingFilter: false, // this gets big... just use the filter sidebar and the embedded filter for vendor names
                menuTabs: ['filterMenuTab'],
                // below is from object_grid for company_tag_list
                enableRowGroup: false,
                enablePivot: false,
                filterParams: {  // options for buttons are 'apply', 'reset', 'clear', 'cancel' -- different ones make sense for different types of filters
                    buttons: ['reset'],
                    closeOnApply: false,
                    suppressSorting: true,  // suppress built-in sorting (it will sort filter options by ID, which is not what we want... we will do sorting below)
                    refreshValuesOnOpen: true,
                    cellRenderer: 'companyTagSingleRenderer',
                    values: params => { // need to get values from refData on columnDef
                        console.log('params in lookup filterParams.values', params);
                        //const vals = Object.keys(params.colDef.refData);  // make sure you always have refData?  sort by values, alphabetically?
                        const vals = sortBy(Object.entries(params.colDef.refData).map(([k, v]) => ({key: k, value: v})), (o) => String(o.value).toLocaleLowerCase()).map(e => e.key);
                        params.success(vals);  // that returns the values to the filter dropdown
                    },
                },
                onCellDoubleClicked: (params) => {
                    console.log('onCellDoubleClicked', params);
                },
                // headerComponent: 'compItemHeaderRenderer',
                cellRenderer: 'companyTagListRenderer',
            }
            const buildTagColDef = (colDef) => {
                return {
                    ...defaultTagColDef,
                    ...colDef,
                };
            };
            const allTagsColDef = buildTagColDef({
                headerName: 'All Tags',
                headerTooltip: 'All Vendor Tags',
                field: ALL_TAGS_ON_VENDOR_KEY,
                // type: [
                //     'tagCategory',
                // ],
                refData: allTagNamesById,  // our filter renderer uses this
                columnGroupShow: 'closed',  // only show this one when CLOSED
            });
            const tagCatColDefs = tagCats.map((cat) => {
                const colKey = getTagCatColKey(cat.id);
                const tagsForCat = tagsByCatId[cat.id] || {};
                const refData = Object.fromEntries(tagsForCat.map(t => [t.id, t.name]));
                return buildTagColDef({
                    headerName: cat.name,
                    headerTooltip: cat.name,
                    field: colKey,
                    // type: [
                    //     'tagCategory',
                    // ],
                    refData: refData,  // our filter renderer uses this
                    columnGroupShow: 'open',  // show only when the group is open
                });
            });
            const tagCols = [...tagCatColDefs];
            if (this.includeAllTagsCol) {
                tagCols.unshift(allTagsColDef);
            }
            return tagCols;
        },
        buildVendorColDef() {
            return {
                headerName: `Vendors (${Object.keys(this.vendorsById).length})`,
                field: 'name',
                pinned: 'left',
                lockPosition: true,
                lockVisible: true,
                resizable: true,
                wrapText: true,
                autoHeight: true,
                maxWidth: 300,
                cellStyle: {
                    wordBreak: 'break-word',  // normal for ag-grid with wrapText is 'break-all'
                    lineHeight: 'normal',  // otherwise is really big for ag-grid
                },
                filter: 'agTextColumnFilter',
                // WORKS, but try to use the in-header filter instead... that looks better for now
                floatingFilter: false,  // if you have floatingFilter then you can probably suppressMenu on the column since you only show filter there
                floatingFilterComponent: 'vendorFloatingFilterComponent',
                floatingFilterComponentParams: {
                    suppressFilterButton: true,  // hide the normal button that opens the filter... not needed here
                },
                filterParams: {
                    buttons: ['reset'],
                    // closeOnApply: true,  // need the apply button showing for this to work
                    suppressAndOrCondition: true,
                    debounceMs: 300,
                },
                suppressMenu: true,  // if we have floating filter, should not need menu
                menuTabs: ['filterMenuTab'],
                suppressColumnsToolPanel: true,
                // cellRenderer: (params) => {
                //     const url = params.data?.url || '';
                //     return (params.value && url) ? `<a href="${url}" target="_blank">${params.value}</a>` : params.value;
                // },
                cellRenderer: 'vendorColRenderer',
                // cellRendererParams: {
                //     suppressCount: true,
                // },
                headerComponent: 'vendorColHeaderRenderer',  // has integrated filter box so we don't need floating filters for now
                headerClass: 'bg-secondary-100 font-size-110',
                cellClass: 'font-weight-bold',
                //width: 200,
            };
        },
        buildCompItemColDefs(compItems) {
            // BUILD AND RETURN (but don't set) column definitions for the grid
            // TODO - add another layer of groups, for each type/category
            // TODO - add a summary column at the beginning, with overall status for each vendor, show/hide it when group is open/closed
            const columnDefs = [
                ...compItems.map(ci => {
                    const colKey = getColKey(ci.id);
                    return {
                        headerName: ci.title,
                        headerTooltip: ci.title,
                        field: colKey,
                        type: [
                            'compItem',
                        ],
                        headerClass: 'text-center bg-primary-100',
                        // cellClass: 'text-center',
                        cellClass: (params) => {
                            // return object with CSS styles
                            const needStatusKey = params.value?.req_status?.need_status_key?.toLowerCase() || '';
                            const reqStatusKey = params.value?.req_status?.key?.toLowerCase() || '';
                            const hasReq = !!params.value?.req_id;
                            const reqStatusVariant = params.value?.req_status?.variant || '';  // set based on our OverallStatus types
                            return [
                                'text-center',
                                'comp-item-status-cell',
                                (needStatusKey ? `need-status-${needStatusKey}` : ''),
                                (reqStatusKey ? `req-status-${reqStatusKey}` : ''),
                                (hasReq && reqStatusVariant ? `bg-${reqStatusVariant}-100` : ''),
                            ];  // return a string or array of strings
                        },
                        menuTabs: [],  // add 'filterMenuTab' later when you have filters for these
                        // FILTERS -- make a special filter that has things to filter by OverallStatus, NeedStatus, VCI Status
                        toolPanelClass: 'bg-primary-100',
                        resizable: true,
                        maxWidth: 200,
                        autoHeight: true,
                        wrapText: true,
                        suppressMenu: true,
                        onCellClicked: (params) => {
                            console.log('onCellClicked', params);
                            //const statusObj = params.data[colKey];
                            // if (statusObj) {
                            //     this.showStatusDetails(statusObj);
                            // }
                        },
                        onCellDoubleClicked: (params) => {
                            console.log('onCellDoubleClicked', params);
                        },
                        headerComponent: 'compItemHeaderRenderer',
                        cellRenderer: 'reqStatusRenderer',
                        cellRendererParams: {
                            compItemId: ci.id,
                        },
                        //width: 200,
                        // EXTRA DATA - this might get removed in some places?
                    }
                }),
            ];
            return columnDefs;
        },

        // GRID CALLBACKS
        async onGridReady(event) {
            // do things now that grid is ready - this is called when we get gridReady event from grid
            // and that only happens after we set this.gridCanMount = true
            console.log('HELLO FROM this.onGridReady'); 
            console.log(event);
            await this.$nextTick();  // waits here...
            this.gridApi = this.gridOptions.api;
            this.gridColumnApi = this.gridOptions.columnApi;     
            this.afterGridAndDataReady();
        },
        afterGridAndDataReady() {
            // another hook point... or get rid of this and just put it inside onGridReady()
        },
        firstDataRendered(event) {
            console.log('firstDataRendered', event);
            this.autoSizeAll();
        },
        onFilterChanged(event) {
            const filterModel = event.api.getFilterModel();
            // if you have an tag col with NO TAGS SELECTED, then you need to handle that specially here
            // because ag-grid will just ignore that filter (treat it like no filter at all) but we want to filter for things with NO TAGGINGS FOR THAT TAG
            // filterModel is an object keyed by colKey with {filterType, values} for each filter - for tag cols with nothing selected, we WILL have a key, and values will be empty array
            // TODO -- do we really want this?  it matches behavior in vendor grid, but is it correct?  Think about set logic...
            // TODO -- now how do we actually set it???  need to do external filter???
            // need to make a custom filter....https://www.ag-grid.com/archive/25.3.0/vue-grid/component-filter/
            console.log('onFilterChanged', event, filterModel);
        },
        autoSizeAll() {
            const skipHeader = true;
            if (this.gridColumnApi) {
                this.gridColumnApi.autoSizeAllColumns(null, skipHeader);  // there are other methods for only autoSize one or multiple
            }
        },
        getExcelStatusExcelContent(vciStatus) {
            // TODO - gotta fix this
            // helper for below - takes a single cell VendorCompItemStatus object
            // should return a string of text to use in Excel export
            const statusLabel = vciStatus.overall_status?.label || '';
            const title = vciStatus.title;
            // return '"' + [title, statusLabel].join(' - ') + '"';
            return [title, statusLabel].join(' - ');
        },
        exportExcel() {
            // see docs: https://www.ag-grid.com/archive/25.3.0/vue-grid/excel-export-api/#interfaces
            const excelExportParams = {
                autoConvertFormulas: true,
                wrapText: true,
                processCellCallback: (params) => {
                    // this should return a string to use in the export 
                    // cell values FOR COMP ITEM COLS are arrays of objects, each object has { details, id, loading, status: VendorCompItemStatus }
                    //console.log('processCellCallback params for excel export', params);
                    const colKey = params.column.colId;
                    if (colKey === 'name') {
                        // this is the vendor name col
                        return params.value;
                    } else if (isCompItemColKey(colKey)) {
                        if (params.value && Array.isArray(params.value) && params.value.length) {
                            const vciStatusObjects = params.value.map(i => i.status).filter(s => !!s);
                            return vciStatusObjects.map(this.getExcelStatusExcelContent).join(' | ');  // &Char(10)& is carriage return in Excel
                        } else {
                            return '';
                        }
                    } else if (isTagColKey(colKey)) {
                        // will include the one for ALL_TAGS
                        const tagIds = Array.isArray(params.value) ? params.value : [];
                        const tagNames = tagIds.map(t => this.companyTagsById[t]?.name).filter(n => !!n);
                        return tagNames.join(', ');
                    } else {
                        return params.value;
                    }
                },
            };
            this.gridApi.exportDataAsExcel(excelExportParams);
            // this.gridApi.exportDataAsExcel();
        },

        // SIDEBAR STUFF
        showStatusSidebar(vciStatus) {
            console.log('showStatusSidebar', vciStatus);
            this.sidebarActiveVendorCompItem = vciStatus;
            this.sidebarShowing = true;
        },
        closeStatusSidebar() {
            // just set the v-model to close, then the @hidden handler will reset things
            this.sidebarShowing = false;
        },
        resetStatusSidebar() {
            this.sidebarShowing = false;
            this.sidebarActiveVendorCompItem = null;
        },
        async handleVciUpdated(payload) {
            // called from @updated OR @deleted on the sidebar for a SINGLE VCI - need to refresh this vendor
            console.log('handleVciUpdated', payload);  // { id, vendor_id, comp_item_id, vendor_comp_item_id }  // NOTE - id might not always be the VCI object id?  might be composite if VCI doesn't exist yet
            const vendorIdToRefresh = payload.vendor_id || this.sidebarActiveVendorCompItem?.vendor_id;
            await this.refreshSingleVendor(vendorIdToRefresh);
            // this.closeStatusSidebar();  // actually feels right to keep this open here
        },
        handleSidebarShown() {
            console.log('handleSidebarShown');
        },
        handleSidebarHidden() {
            console.log('handleSidebarHidden');
            this.resetStatusSidebar();
        },
        handleSidebarChange(val) {
            console.log('handleSidebarChange', val);
        },
        /// ADDING VENDOR COMP ITEM

        // TODO - need to refresh data for that vendor in the grid after adding a new VCI

        startAddSpecificCompItem(rowData, compItemId) {
            // TODO - update this
            // triggered by a single cell -- pass in the rowData (to be consistent with other version of this) AND the specific compItemId to add
            // need to figure out if its possible first!  this is actually kinda hard with current components - need to show the form first
            // should make the form show some nice error message if it tries to open with a pre-set comp item (in create mode) that is not available
            // TODO - implement that (above) then you can use it here
            console.log('startAddSpecificCompItem', rowData, compItemId);
            if (this.addingVendorId) {
                console.error('Already adding a VendorCompItem - cannot start a new one');
                return;
            }
            this.addVciSpecificCompItem = compItemId;
            this.addVciActiveRowData = rowData;
            this.$nextTick(() => {
                this.addVciModalShowing = true;
            });
        },

        startAdd(rowData) {
            // SHOW ADD FORM FOR A VENDOR - ANY COMP ITEMS
            console.log('startAdd', rowData);
            if (this.addingVendorId) {
                console.error('Already adding a VendorCompItem - cannot start a new one');
                return;
            }
            this.addVciSpecificCompItem = '';  // make sure this is blank for this one
            this.addVciActiveRowData = rowData;
            this.$nextTick(() => {
                this.addVciModalShowing = true;
            });
        },
        handleVciModalHidden() {
            this.addVciSpecificCompItem = '';
            this.addVciActiveRowData = {};
        },
        closeVciModal() {
            this.addVciModalShowing = false;  // the @hidden handler will then reset things
        },
        handleAddVciLoadingStatus(isLoading) {
            // from @loading-status on the add form -- likely nothing to do here?  maybe lock things?
            console.log('handleAddVciLoadingStatus', isLoading);
        },
        async handleAddVciSaved(returnData) {
            // called from the @saved event on add form -- could also listen for the @updated or @created -- but this covers both
            // returnData is the new data for the thing we created/updated -- we'll ignore that and just refetch things here
            console.log('handleAddVciSaved', returnData);
            const vendorIdToRefresh = this.addingVendorId;  // or get from returnData?
            await this.refreshSingleVendor(vendorIdToRefresh);
            this.closeVciModal();
        },
    }
}
</script>

<style lang="scss">

// @import "../../css/spl-variables.scss";
@import '../../css/spl-variables.scss';
@import "~ag-grid-community/src/styles/ag-grid.scss";
@import "~ag-grid-community/src/styles/ag-theme-alpine/sass/ag-theme-alpine-mixin";
//@import "../../css/supplios-ag-grid.scss";  // has @mixin ag-supplios-generic-mixin {

.ag-theme-compliance-grid {
    @include ag-theme-alpine((
        // PUT THEME PARAMETERS HERE - this works, but seems to duplicate all the CSS!

        //alpine-active-color: deeppink
        //background-color: $gray-200,
        //background-color: #fff,
        // odd-row-background-color: null,
        // header-background-color: $gray-200,
        // row-border-color: $gray-200,  // I made it match background-color so empty variant cells look normal.   normal value:  ag-derived(secondary-border-color),
        // grid-size: 5px,  // normal for alpine seems to be 6px.  its not bad, but should shrink horizontal padding
        // // Horizontal padding for grid and header cells (vertical padding is not set explicitly, but inferred from row-height / header-height
        cell-horizontal-padding: ag-derived(grid-size, $times: 1),  // originally was grid-size, $times: 3
        //cell-horizontal-padding: ag-derived(grid-size, $times: 3),


        // Fonts
        // font-family: ("Helvetica Neue", sans-serif),
        // font-size: 12px,
    ));
}

.comp-item-status-grid {

    

    .ag-header-cell-label > .ag-header-cell-text {
        white-space: normal !important;   // basically text-wrap class 
    }

    .ag-root-wrapper {
        border-radius: 0.75rem;
    }

    .ag-cell.comp-item-status-cell {

        // this works... we do this here so we can have our cell renderer cover the entire cell
        // but maybe there will be hidden problems doing this and not setting this using ag-grid variables?
        padding-left: 0;
        padding-right: 0;

        span.need-status-chip {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            gap: 0.25rem;
            border: 1px solid rgba(0, 0, 0, 0.1);
            background-color: rgba(235, 235, 235, 0.35);
            border-radius: 1rem;
            padding: 3px 5px;
            font-size: 0.8em;
        }

        // NEED STATUS - make sure these stay up-to-date with backend
        // &.need-status-mandatory {
        //     background: linear-gradient(90deg, var(--gray-500) 0%, var(--gray-500) 4px, var(--gray-150) 8px, var(--gray-150) 100%);
        // }
        // &.need-status-optional {
        //     background: linear-gradient(90deg, var(--gray-300) 0%, var(--gray-300) 4px, var(--gray-100) 8px, var(--gray-100) 100%);
        // }
        // &.need-status-excluded {
        //     background: linear-gradient(90deg, var(--gray-100) 0%, var(--gray-100) 4px, var(--gray-50) 8px, var(--gray-50) 100%);
        // }
        // &.need-status-not_set {
        //     background: transparent;
        // }
        // REQ STATUS - make sure these stay up-to-date with backend
        // &.req-status-valid {
        //     background-color: var(--green-100) !important;
        // }
        // &.req-status-invalid {
        //     background-color: var(--red-100) !important;
        // }
        // &.req-status-incomplete {
        //     background-color: var(--gray-100) !important;
        // }
        // &.req-status-missing {
        //     background-color: var(--gray-100) !important;
        // }
        // &.req-status-excluded {
        //     background-color: var(--gray-100) !important;
        // }
        // &.req-status-not_set {
        //     background-color: var(--gray-100) !important;
        // }
    }


    .empty-compitem-cell {
        // &:hover {
        //     .empty-compitem-button {
        //         opacity: 1;
        //         background-color: var(--gray-500);
        //         color: #fff;
        //     }
        // }
        .empty-compitem-button {
            opacity: 0.35;
            transition: ease 0.3s;
            padding: 3px 1rem; 
            word-break: break-word;

            &:hover {
                opacity: 1;
                background-color: var(--primary-500);
                color: #fff;
            }
        }
    }

}
</style>